import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import {
  TranslocoPipe,
  TranslocoService,
  translateSignal,
} from '@jsverse/transloco';
import { toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, skip, Subscription } from 'rxjs';
import { WorkFlowConfiguration } from '../workflow.model';
import {
  CustomWorkflowApiError,
  localizedGroup,
} from '../custom-workflow/custom-workflow.model';
import {
  CUSTOM_WORKFLOW_ICON_UPLOAD_LABELS,
  WORKFLOW_CONFIGURATIONS_COLUMNS,
  WORKFLOW_CONFIGURATIONS_COLUMNS_WITH_KIND,
  WORKFLOW_CONFIGURATIONS_VIEW_DETAILS_ACTION,
  WORKFLOW_CONFIGURATIONS_FILTER_TABS,
} from './workflow-configuration.constants';
import { WorkflowService } from '../workflow.service';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { ButtonComponent } from '../../../../../ui-components/lib/button/button.component';
import { PaginatorComponent } from '../../../../../ui-components/lib/paginator/paginator.component';
import { SearchFieldComponent } from '../../../../../ui-components/lib/search-field/search-field.component';
import {
  initialSearchTerm,
  SearchService,
} from '../../../../../ui-components/lib/search-field/search.service';
import { SelectComponent } from '../../../../../ui-components/lib/select/select.component';
import { TableComponent } from '../../../../../ui-components/lib/table/table.component';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import { TruncatePipe } from '../../../../../ui-components/pipes/truncate/truncate.pipe';
import { UploadFilesComponent } from '../../../../../ui-components/lib/upload-files/upload-files.component';
import { Attachment } from '../../../../../ui-components/lib/upload-files/attachment.interface';
import { ValidationMessageComponent } from '../../../../../ui-components/lib/validation-message/validation-message.component';
import { OptionItem } from '../../../../../ui-components/lib/option.interface';
import { ApiResponse } from '../../../../common/models/api-response.model';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../common/constants/pagination.constants';
import { ADMIN_ROUTES } from '../../../../common/constants/admin.routing.constants';
import { uploadFileUrls } from '../../../../common/constants/upload-file-url';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { ActionListComponent } from '../../../../common/components/action-list/action-list.component';
import { ActionList } from '../../../../common/models/action-list.model';
import { SearchEnum } from '../../../../common/enums/search.enum';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { CustomWorkflowService } from '../custom-workflow/custom-workflow.service';
import { ModalServiceService } from '../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import { FormService } from '../../../../../form-builder/services/form/form.service';
import { DualLangInputComponent } from '../../../../../form-builder/lib/dual-lang-input/dual-lang-input.component';
import { StatusPipe } from '../../../../../ui-components/pipes/status/status.pipe';

@Component({
  selector: 'lib-workflow-configuration',
  imports: [
    NgTemplateOutlet,
    BadgeComponent,
    ButtonComponent,
    TableComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    PaginatorComponent,
    AvatarComponent,
    TruncatePipe,
    ActionListComponent,
    SearchFieldComponent,
    SelectComponent,
    UploadFilesComponent,
    ValidationMessageComponent,
    DualLangInputComponent,
    StatusPipe,
  ],
  providers: [provideSystemTabsLayout()],
  templateUrl: './workflow-configuration.component.html',
  styleUrl: './workflow-configuration.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkflowConfigurationComponent implements OnInit {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  private readonly featureFlagsService = inject(FeatureFlagsService);
  /** Tables_new_design moves the search from the panel header into the table toolbar. */
  protected readonly searchInToolbar = computed(
    () =>
      this.floating() &&
      this.featureFlagsService.getFeatureFlagValue(
        FEATURE_FLAGS.TABLE.ui_enhancement
      )
  );
  readonly workflowBuilderEnabled =
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOW_CONFIGURATION.workflow_builder
    );
  transloco = inject(TranslocoService);
  workflowService = inject(WorkflowService);
  private readonly customWorkflowService = inject(CustomWorkflowService);
  private readonly modalService = inject(ModalServiceService);
  private readonly toaster = inject(ToasterService);
  private readonly formService = inject(FormService);
  router = inject(Router);
  tenantService = inject(TenantService);
  private readonly searchService = inject(SearchService);

  readonly deleteTemplate = viewChild<TemplateRef<unknown>>('deleteConfirm');
  readonly createTemplate = viewChild<TemplateRef<unknown>>('createModal');
  pendingDelete: WorkFlowConfiguration | null = null;

  readonly uploadFileUrls = uploadFileUrls;
  readonly noFiles: Attachment[] = [];
  private readonly iconUploadLabelValues = translateSignal(
    Object.values(CUSTOM_WORKFLOW_ICON_UPLOAD_LABELS)
  );
  iconUploadLabels = computed(() => {
    const values = this.iconUploadLabelValues() as string[];
    return Object.fromEntries(
      Object.keys(CUSTOM_WORKFLOW_ICON_UPLOAD_LABELS).map((name, i) => [
        name,
        values[i],
      ])
    );
  });

  isSaving = signal(false);
  categories = signal<OptionItem[]>([]);

  createForm = new FormGroup({
    name: localizedGroup(),
    category_id: new FormControl<number | null>(null, Validators.required),
    icon: new FormControl<number | null>(null),
  });

  isLoading = signal<boolean>(false);
  currentPage = signal<number>(1);
  currentSize = signal<number>(25);
  totalPages = signal<number>(20);
  workflowsCount = signal<string>('00');

  configurationList = signal<WorkFlowConfiguration[]>([]);
  tableRows = computed(() =>
    this.configurationList().map(row => ({
      ...row,
      actions: this.actionsFor(row),
    }))
  );

  private readonly viewDetailsActionBase: ActionList = {
    ...WORKFLOW_CONFIGURATIONS_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(
      WORKFLOW_CONFIGURATIONS_VIEW_DETAILS_ACTION.label
    ),
  };

  viewDetailsAction = signal<ActionList>(this.viewDetailsActionBase);

  private readonly baseColumns = this.workflowBuilderEnabled
    ? WORKFLOW_CONFIGURATIONS_COLUMNS_WITH_KIND
    : WORKFLOW_CONFIGURATIONS_COLUMNS;

  columns = signal<TableColumns[]>(
    this.baseColumns.map(col => ({
      ...col,
      header: col.header ? this.transloco.translate(col.header) : '',
    }))
  );
  tableFilterTabs = signal<TableFilterTab[]>(
    WORKFLOW_CONFIGURATIONS_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );

  readonly searchInputWidth = SearchEnum.searchInputWidth;
  searchTerm = signal(initialSearchTerm());
  isMoreThanThreeCharacters = signal(
    this.searchService.isMinLength(this.searchTerm())
  );
  private readonly searchTerm$ = toObservable(this.searchTerm);
  subscriptions = new Subscription();

  ngOnInit() {
    this.getWorkFlowConfigurations();
    this.searchTermWrapper();
    if (this.workflowBuilderEnabled) {
      this.customWorkflowService.categories().subscribe(res =>
        this.categories.set(
          res.data.map((c: OptionItem) => ({
            label: c.label,
            value: c.value,
          }))
        )
      );
    }
  }

  searchTermWrapper() {
    this.subscriptions.add(
      this.searchTerm$
        .pipe(skip(1), debounceTime(300), distinctUntilChanged())
        .subscribe(term => {
          this.handleSearchTerm(term);
        })
    );
  }

  onSearchTermChange(term: string) {
    this.searchTerm.set(term);
  }

  handleSearchTerm(term: string): void {
    this.currentPage.set(1);
    if (
      this.searchService.applySearchTerm(term, this.isMoreThanThreeCharacters)
    ) {
      this.getWorkFlowConfigurations();
    }
  }

  handlePagination(page: number) {
    this.currentPage.set(page);
    this.getWorkFlowConfigurations();
  }

  changeSize(size: number) {
    this.currentSize.set(size);
    this.currentPage.set(1);
    this.getWorkFlowConfigurations();
  }

  getWorkFlowConfigurations() {
    this.isLoading.set(true);
    const kind = this.workflowBuilderEnabled ? undefined : 'built_in';
    this.workflowService
      .getWorkflowConfigurationList(
        this.currentPage(),
        this.currentSize(),
        kind,
        this.searchService.checkSearchLength(this.searchTerm())
      )
      .subscribe({
        next: (res: ApiResponse<WorkFlowConfiguration>) =>
          this.handleWorkflowConfigurationResponse(res),
        error: () => this.isLoading.set(false),
      });
  }

  handleWorkflowConfigurationResponse(res: ApiResponse<WorkFlowConfiguration>) {
    this.configurationList.set(res.data);
    this.currentPage.set(res.meta.current_page);
    this.workflowsCount.set(res.meta.total.toString());
    this.totalPages.set(res.meta.last_page);
    this.isLoading.set(false);
  }

  open(row: WorkFlowConfiguration) {
    const path = this.workflowBuilderEnabled
      ? `${ADMIN_ROUTES.WORKFLOW.CUSTOM_WORKFLOWS}/${row.id}`
      : `${ADMIN_ROUTES.WORKFLOW.CONFIGURATIONS}/${row.id}`;
    this.router.navigateByUrl(this.tenantService.adminPath(path));
  }

  duplicate(row: WorkFlowConfiguration) {
    this.customWorkflowService.duplicate(row.id).subscribe({
      next: (res: any) => {
        this.router.navigateByUrl(
          this.tenantService.adminPath(
            `${ADMIN_ROUTES.WORKFLOW.CUSTOM_WORKFLOWS}/${res.data.id}`
          )
        );
      },
    });
  }

  actionsFor(row: WorkFlowConfiguration): ActionList[] {
    if (!this.workflowBuilderEnabled) {
      return [
        {
          ...this.viewDetailsActionBase,
          routerLinks: [
            this.tenantService.adminPath(ADMIN_ROUTES.WORKFLOW.CONFIGURATIONS),
            row.id.toString(),
          ],
        },
      ];
    }

    const actions: ActionList[] = [
      {
        key: 'view_details',
        label: this.transloco.translate('view_details_action_item'),
        hasIcon: true,
        hasDivider: false,
        labelCustomStyle: '',
        hasToggle: false,
        toggleValue: false,
        isLink: false,
      },
      {
        key: 'duplicate',
        label: this.transloco.translate('duplicate_action_item'),
        hasIcon: true,
        hasDivider: false,
        labelCustomStyle: '',
        hasToggle: false,
        toggleValue: false,
        isLink: false,
      },
    ];

    if (row.kind === 'dynamic' && row.requests_count === 0) {
      actions.push({
        key: 'delete',
        label: this.transloco.translate('delete_action_item'),
        hasIcon: true,
        hasDivider: false,
        labelCustomStyle: 'text-destructive-600',
        hasToggle: false,
        toggleValue: false,
        isLink: false,
      });
    }

    return actions;
  }

  viewWorkflowDetails(workflow: any) {
    this.open(workflow);
  }

  getViewActionList(workflowId: number): ActionList[] {
    this.viewDetailsAction().routerLinks = [
      this.tenantService.adminPath(`${ADMIN_ROUTES.WORKFLOW.CONFIGURATIONS}`),
      workflowId.toString(),
    ];
    return [this.viewDetailsAction()];
  }

  onAction(key: string, row: WorkFlowConfiguration) {
    if (key === 'view_details') this.open(row);
    if (key === 'duplicate') this.duplicate(row);
    if (key === 'delete') {
      this.pendingDelete = row;
      this.modalService.modalContent.set({
        isOpen: true,
        hasCloseIcon: false,
        title: '',
        customizeContent: this.deleteTemplate(),
      });
    }
  }

  cancelDelete() {
    this.pendingDelete = null;
    this.modalService.modalContent.set({ isOpen: false });
  }

  confirmDelete() {
    const row = this.pendingDelete;
    this.cancelDelete();
    if (!row) return;
    this.customWorkflowService.remove(row.id).subscribe(() => {
      this.toaster.add(
        this.transloco.translate('custom_workflow_deleted_toast'),
        '',
        '',
        'success',
        true,
        true
      );
      this.getWorkFlowConfigurations();
    });
  }

  actionListItemClick(action: string, row: WorkFlowConfiguration) {
    if (action === 'view') {
      this.router.navigate([
        this.tenantService.adminPath(`${ADMIN_ROUTES.WORKFLOW.CONFIGURATIONS}`),
        row.id,
      ]);
    }
  }

  openCreate() {
    this.createForm.reset();
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: '',
      customizeContent: this.createTemplate(),
      width: '40',
    });
  }

  closeCreate() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  submitCreate() {
    this.formService.syncDualLangControls(this.createForm);
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    const value = this.createForm.getRawValue();
    this.isSaving.set(true);
    this.customWorkflowService
      .create({
        name: value.name,
        category_id: value.category_id as number,
        icon: value.icon,
      })
      .subscribe({
        next: (res: any) => {
          this.isSaving.set(false);
          this.closeCreate();
          this.router.navigateByUrl(
            this.tenantService.adminPath(
              `${ADMIN_ROUTES.WORKFLOW.CUSTOM_WORKFLOWS}/${res.data.id}`
            )
          );
        },
        error: (error: CustomWorkflowApiError) => {
          this.isSaving.set(false);
          this.formService.addControlErrors(error, this.createForm);
        },
      });
  }
}
